import React from 'react';
import { cn } from '@/lib/utils';

/**
 * OnlineStatus - Shows a green/gray dot indicating user's online status
 * @param {boolean} isOnline - Whether the user is currently online
 * @param {string} className - Additional CSS classes
 * @param {string} size - Size variant: 'sm' | 'md' | 'lg'
 */
const OnlineStatus = ({ isOnline, className, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3'
  };

  return (
    <span
      className={cn(
        'inline-block rounded-full flex-shrink-0',
        sizeClasses[size],
        isOnline 
          ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]' 
          : 'bg-gray-400',
        className
      )}
      title={isOnline ? 'Online' : 'Offline'}
    />
  );
};

/**
 * UserAvatar - User avatar with optional online status indicator
 */
export const UserAvatar = ({ 
  name, 
  isOnline, 
  showStatus = true, 
  size = 'md',
  className 
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  };

  const statusPositionClasses = {
    sm: '-bottom-0.5 -right-0.5',
    md: '-bottom-0.5 -right-0.5',
    lg: 'bottom-0 right-0'
  };

  return (
    <div className={cn('relative inline-flex', className)}>
      <div className={cn(
        'rounded-full bg-primary/10 flex items-center justify-center',
        sizeClasses[size]
      )}>
        <span className="font-semibold text-primary">
          {name?.charAt(0)?.toUpperCase() || '?'}
        </span>
      </div>
      {showStatus && (
        <OnlineStatus 
          isOnline={isOnline} 
          size="sm"
          className={cn(
            'absolute border-2 border-background',
            statusPositionClasses[size]
          )}
        />
      )}
    </div>
  );
};

export default OnlineStatus;
